import { useState } from "react";
import { Button } from "@/components/ui/Button";
import type { KratosFlow } from "@/lib/kratos";
import {
  createFlow,
  firstFlowError,
  KratosSessionRefreshError,
  kratosErrorCopy,
  nodeByName,
  nodeMessages,
  submitFlow,
} from "@/lib/kratos";

/**
 * Set a new password via a Kratos settings flow. Used by recovery
 * (Kratos hands a privileged settings flow after a valid code) and by
 * the account security tab. Settings flows demand a recently
 * authenticated Kratos session — `onSessionRefreshRequired` lets the
 * caller prompt an inline re-auth instead of dead-ending.
 *
 * `requireCurrentPassword` (the security tab) adds a "current
 * password" field, verified by completing a `refresh=true` login flow
 * before the change — which also re-ups the privileged session, so
 * the 15-minute re-auth dead-end can't interrupt the save. Recovery
 * leaves it off: the user proved ownership with the emailed code and
 * may not know the old password at all.
 */
export function KratosPasswordForm({
  initialFlow,
  submitLabel = "Set new password",
  onSaved,
  onSessionRefreshRequired,
  requireCurrentPassword = false,
  testIdPrefix = "kratos-password",
}: {
  initialFlow: KratosFlow;
  submitLabel?: string;
  onSaved: () => void | Promise<void>;
  onSessionRefreshRequired?: () => void;
  requireCurrentPassword?: boolean;
  testIdPrefix?: string;
}) {
  const [flow, setFlow] = useState(initialFlow);
  const [currentPassword, setCurrentPassword] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const verifyCurrentPassword = async (): Promise<boolean> => {
    // A completed `refresh=true` login flow IS the verification —
    // Kratos rejects a wrong password with a validation message. The
    // refresh flow pre-fills the identifier for the live session.
    const refreshFlow = await createFlow("login", { refresh: "true" });
    const identifier = String(
      nodeByName(refreshFlow, "identifier")?.attributes.value ?? "",
    );
    if (!identifier) {
      setError(
        "Couldn't verify your current password — sign out and back in, then retry.",
      );
      return false;
    }
    const outcome = await submitFlow("login", refreshFlow, {
      method: "password",
      identifier,
      password: currentPassword,
    });
    if (outcome.kind === "success") return true;
    setError(
      firstFlowError(outcome.flow) ?? "Current password is incorrect.",
    );
    return false;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    if (password !== confirmPassword) {
      setError("New password and confirmation don't match.");
      return;
    }
    setSubmitting(true);
    try {
      if (requireCurrentPassword && !(await verifyCurrentPassword())) {
        return;
      }
      const outcome = await submitFlow("settings", flow, {
        method: "password",
        password,
      });
      if (outcome.kind === "flow") {
        setFlow(outcome.flow);
        if (outcome.flow.state === "success") {
          setCurrentPassword("");
          setPassword("");
          setConfirmPassword("");
          await onSaved();
          return;
        }
      } else {
        setCurrentPassword("");
        setPassword("");
        setConfirmPassword("");
        await onSaved();
      }
    } catch (err) {
      if (err instanceof KratosSessionRefreshError && onSessionRefreshRequired) {
        onSessionRefreshRequired();
        return;
      }
      setError(kratosErrorCopy(err));
    } finally {
      setSubmitting(false);
    }
  };

  const flowError = firstFlowError(flow);
  const passwordMessages = nodeMessages(flow, "password");
  const inputClass =
    "rounded border border-rule bg-canvas px-3 py-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-accent";

  return (
    <form
      data-testid={`${testIdPrefix}-form`}
      className="flex flex-col gap-3"
      onSubmit={handleSubmit}
    >
      {requireCurrentPassword ? (
        <label className="flex flex-col gap-1 text-xs">
          <span className="font-display uppercase tracking-wider text-ink-muted">
            Current password
          </span>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            required
            data-testid={`${testIdPrefix}-current`}
            className={inputClass}
          />
        </label>
      ) : null}
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          New password
        </span>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          required
          minLength={8}
          data-testid={`${testIdPrefix}-input`}
          className={inputClass}
        />
        {passwordMessages.map((message) => (
          <span key={message} className="font-mono text-[0.65rem] text-destructive">
            {message}
          </span>
        ))}
      </label>
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Confirm new password
        </span>
        <input
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          required
          minLength={8}
          data-testid={`${testIdPrefix}-confirm`}
          className={inputClass}
        />
      </label>
      {flowError ? (
        <p data-testid={`${testIdPrefix}-error`} className="font-mono text-xs text-destructive">
          {flowError}
        </p>
      ) : null}
      {error ? (
        <p
          data-testid={`${testIdPrefix}-client-error`}
          className="font-mono text-xs text-destructive"
        >
          {error}
        </p>
      ) : null}
      <Button
        type="submit"
        disabled={submitting}
        data-testid={`${testIdPrefix}-submit`}
        className="self-end"
      >
        {submitting ? "Saving…" : submitLabel}
      </Button>
    </form>
  );
}
